import { z } from "zod";
import { SystemEntity } from "./systemEntities";

/**
 * Saved layout for a task type. The review page fills this spec with one
 * task's data; it does not invent a layout. See docs/task-type-views.md.
 */

export const TASK_TYPE_VIEW_VERSION = 1 as const;

export const TASK_TYPE_VIEW_MATCH_TARGETS = [
  "product_variant",
  "supplier",
] as const;

export type TaskTypeViewMatchTarget =
  (typeof TASK_TYPE_VIEW_MATCH_TARGETS)[number];

/** Scalar schema field types a `field` placement may address. */
export const TASK_TYPE_VIEW_SCALAR_FIELD_TYPES = [
  "string",
  "text",
  "integer",
  "float",
  "numeric",
  "boolean",
  "date",
  "time",
  "datetime",
  "enum",
  "currency",
  "url",
] as const;

export type TaskTypeViewScalarFieldType =
  (typeof TASK_TYPE_VIEW_SCALAR_FIELD_TYPES)[number];

/**
 * An `entity_reference` is a `{ entityType, entityId }` object, not a scalar.
 * A `field` placement may address it. The page renders the record instead of
 * a text box.
 */
export const TASK_TYPE_VIEW_ENTITY_REFERENCE_FIELD_TYPE =
  "entity_reference" as const;

/**
 * A `document` field stores `{ entityType: "CORE_DOCUMENT", entityId }`.
 * A `field` placement may address it and renders the document.
 */
export const TASK_TYPE_VIEW_DOCUMENT_FIELD_TYPE = "document" as const;

const TASK_TYPE_VIEW_FIELD_TYPES = new Set<string>([
  ...TASK_TYPE_VIEW_SCALAR_FIELD_TYPES,
  TASK_TYPE_VIEW_ENTITY_REFERENCE_FIELD_TYPE,
  TASK_TYPE_VIEW_DOCUMENT_FIELD_TYPE,
]);

/** Schema field types a `field` placement may address. */
export function isTaskTypeViewFieldType(fieldType: string): boolean {
  return TASK_TYPE_VIEW_FIELD_TYPES.has(fieldType);
}

/** Schema field types a `match` placement may address. */
export const TASK_TYPE_VIEW_MATCH_FIELD_TYPES = ["string", "text"] as const;

export const TASK_TYPE_VIEW_SOURCE_KINDS = [
  "email",
  "document",
  "apInvoice",
  "cashReceipt",
] as const;

export type TaskTypeViewSourceKind =
  (typeof TASK_TYPE_VIEW_SOURCE_KINDS)[number];

/** The anchor or reference entity each source viewer renders. */
export const TASK_TYPE_VIEW_SOURCE_ENTITY: Record<
  TaskTypeViewSourceKind,
  SystemEntity
> = {
  email: SystemEntity.INBOUND_EMAIL,
  document: SystemEntity.CORE_DOCUMENT,
  apInvoice: SystemEntity.AP_INVOICE,
  cashReceipt: SystemEntity.AR_CASH_RECEIPT,
};

/**
 * Whether a source placement may read its id from this schema field. A
 * string holds the id itself. A `document` field (document viewer only), or
 * an `entity_reference` to the matching entity, holds `{ entityType, entityId }`
 * — the same shape a workflow socket wires onto the task input.
 */
export function isTaskTypeViewSourceField(
  kind: TaskTypeViewSourceKind,
  field: {
    fieldType: string;
    isMultiple?: boolean;
    referencedEntityType?: SystemEntity | null;
  },
): boolean {
  if (field.isMultiple) return false;
  if (field.fieldType === "string" || field.fieldType === "text") return true;
  if (field.fieldType === TASK_TYPE_VIEW_DOCUMENT_FIELD_TYPE) {
    return kind === "document";
  }
  if (field.fieldType !== TASK_TYPE_VIEW_ENTITY_REFERENCE_FIELD_TYPE) {
    return false;
  }
  const expected = TASK_TYPE_VIEW_SOURCE_ENTITY[kind];
  return (
    field.referencedEntityType == null ||
    field.referencedEntityType === expected
  );
}

const viewIdSchema = z.string().trim().min(1).max(200);
const viewLabelSchema = z.string().max(500).nullable();
const viewPathSchema = z.array(z.string().trim().min(1).max(100)).min(1).max(6);
const viewModeSchema = z.enum(["read", "edit"]);

const fieldConfigSchema = z.strictObject({
  label: viewLabelSchema,
  path: viewPathSchema,
  mode: viewModeSchema,
});

const matchConfigSchema = z.strictObject({
  label: viewLabelSchema,
  path: viewPathSchema,
  target: z.enum(TASK_TYPE_VIEW_MATCH_TARGETS),
  mode: viewModeSchema,
});

const fieldPlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("field"),
  config: fieldConfigSchema,
});

const matchPlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("match"),
  config: matchConfigSchema,
});

/**
 * Line-list columns are field and match only. A nested lineList is rejected
 * here, before any path check.
 */
const lineListColumnSchema = z.discriminatedUnion("component", [
  fieldPlacementSchema,
  matchPlacementSchema,
]);

/**
 * One extracted column on a supplier document line. The path is relative to
 * a match row and is typed by the author, not checked against schema
 * children: the array field has none so the rest of the match row survives
 * a correction save. A typical path is `extracted.quantity`.
 */
const supplierLineMatchColumnSchema = z.strictObject({
  id: viewIdSchema,
  label: viewLabelSchema,
  path: viewPathSchema,
});

/**
 * Whether a `supplierLineMatch` placement may read its supplier from this
 * schema field: an `entity_reference` to `AP_SUPPLIER` (or untyped), or a
 * string holding the supplier id.
 */
export function isTaskTypeViewSupplierField(field: {
  fieldType: string;
  isMultiple?: boolean;
  referencedEntityType?: SystemEntity | null;
}): boolean {
  if (field.isMultiple) return false;
  if (field.fieldType === "string" || field.fieldType === "text") return true;
  if (field.fieldType !== TASK_TYPE_VIEW_ENTITY_REFERENCE_FIELD_TYPE) {
    return false;
  }
  return (
    field.referencedEntityType == null ||
    field.referencedEntityType === SystemEntity.AP_SUPPLIER
  );
}

/**
 * Rows from Match Supplier Document Lines. The person can remap each row's
 * product and edit the mapped extracted columns. The write stays on the task
 * correction; it does not create a SupplierProduct.
 */
const supplierLineMatchPlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("supplierLineMatch"),
  config: z.strictObject({
    label: viewLabelSchema,
    /** Path to the array of match rows (`lines`, `unmatchedLines`, …). */
    path: viewPathSchema,
    /**
     * Field holding the supplier the rows belong to. The remap picker
     * searches that supplier's translations first, and when the working-copy
     * supplier changes the rows are matched again against the new one.
     * Null means catalog-only remap.
     */
    supplierPath: viewPathSchema.nullable(),
    /**
     * Extraction schema the match node was pinned to. Design-time only: the
     * builder offers its row fields as `extracted.<field>` columns so the
     * author picks from a list instead of recalling key names. The page does
     * not read it.
     */
    columnSchemaDefinitionId: z.uuid().nullable(),
    /** Editable extracted columns. Empty shows identifiers and remap only. */
    columns: z.array(supplierLineMatchColumnSchema).max(12),
  }),
});

const lineListPlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("lineList"),
  config: z.strictObject({
    label: viewLabelSchema,
    path: viewPathSchema,
    columns: z.array(lineListColumnSchema).max(12),
  }),
});

/**
 * Source placements show an existing viewer. They do not write the
 * correction. `anchor` reads the task anchor. `path` reads an id from the
 * working copy. `apInvoice` renders the AP invoice detail panel, the same
 * body the invoice slide-in uses. `cashReceipt` renders the cash receipt
 * detail, the same body as the cash receipts page, without its action footer.
 */
const sourceConfigSchema = z.strictObject({
  label: viewLabelSchema,
  source: z.discriminatedUnion("from", [
    z.strictObject({ from: z.literal("anchor") }),
    z.strictObject({ from: z.literal("path"), path: viewPathSchema }),
  ]),
});

const emailPlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("email"),
  config: sourceConfigSchema,
});

const documentPlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("document"),
  config: sourceConfigSchema,
});

const apInvoicePlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("apInvoice"),
  config: sourceConfigSchema,
});

const cashReceiptPlacementSchema = z.strictObject({
  id: viewIdSchema,
  component: z.literal("cashReceipt"),
  config: sourceConfigSchema,
});

export const TaskTypeViewPlacementSchema = z.discriminatedUnion("component", [
  fieldPlacementSchema,
  lineListPlacementSchema,
  matchPlacementSchema,
  emailPlacementSchema,
  documentPlacementSchema,
  apInvoicePlacementSchema,
  cashReceiptPlacementSchema,
  supplierLineMatchPlacementSchema,
]);

export const TASK_TYPE_VIEW_REGION_COLUMNS = ["main", "aside"] as const;
export type TaskTypeViewRegionColumn =
  (typeof TASK_TYPE_VIEW_REGION_COLUMNS)[number];

/**
 * `main` regions stack on the left. `aside` regions stack on the right and
 * stay in view while the main column scrolls, so a document or email can sit
 * beside the fields it was extracted into. A view with no aside region is a
 * single column.
 */
export const TaskTypeViewRegionSchema = z.strictObject({
  id: viewIdSchema,
  title: z.string().max(500).nullable(),
  column: z.enum(TASK_TYPE_VIEW_REGION_COLUMNS),
  placements: z.array(TaskTypeViewPlacementSchema).max(50),
});

export const TaskTypeViewSchema = z
  .strictObject({
    version: z.literal(TASK_TYPE_VIEW_VERSION),
    regions: z.array(TaskTypeViewRegionSchema).max(20),
  })
  .superRefine((view, ctx) => {
    const ids: string[] = [];
    for (const region of view.regions) {
      ids.push(region.id);
      for (const placement of region.placements) {
        ids.push(placement.id);
        if (
          placement.component !== "lineList" &&
          placement.component !== "supplierLineMatch"
        ) {
          continue;
        }
        for (const column of placement.config.columns) {
          ids.push(column.id);
        }
      }
    }
    if (new Set(ids).size === ids.length) return;
    ctx.addIssue({
      code: "custom",
      message: "View ids must be unique",
    });
  });

export type TaskTypeView = z.infer<typeof TaskTypeViewSchema>;
export type TaskTypeViewRegion = z.infer<typeof TaskTypeViewRegionSchema>;
export type TaskTypeViewPlacement = z.infer<typeof TaskTypeViewPlacementSchema>;
export type TaskTypeViewFieldPlacement = z.infer<typeof fieldPlacementSchema>;
export type TaskTypeViewMatchPlacement = z.infer<typeof matchPlacementSchema>;
export type TaskTypeViewLineListPlacement = z.infer<
  typeof lineListPlacementSchema
>;
export type TaskTypeViewSourceConfig = z.infer<typeof sourceConfigSchema>;
export type TaskTypeViewEmailPlacement = z.infer<typeof emailPlacementSchema>;
export type TaskTypeViewDocumentPlacement = z.infer<
  typeof documentPlacementSchema
>;
export type TaskTypeViewApInvoicePlacement = z.infer<
  typeof apInvoicePlacementSchema
>;
export type TaskTypeViewCashReceiptPlacement = z.infer<
  typeof cashReceiptPlacementSchema
>;
export type TaskTypeViewSourcePlacement =
  | TaskTypeViewEmailPlacement
  | TaskTypeViewDocumentPlacement
  | TaskTypeViewApInvoicePlacement
  | TaskTypeViewCashReceiptPlacement;

export function isTaskTypeViewSourcePlacement(
  placement: TaskTypeViewPlacement,
): placement is TaskTypeViewSourcePlacement {
  return (
    placement.component === "email" ||
    placement.component === "document" ||
    placement.component === "apInvoice" ||
    placement.component === "cashReceipt"
  );
}

export type TaskTypeViewSupplierLineMatchPlacement = z.infer<
  typeof supplierLineMatchPlacementSchema
>;
export type TaskTypeViewSupplierLineMatchColumn = z.infer<
  typeof supplierLineMatchColumnSchema
>;
